import React, { useEffect, useState } from 'react';
import { tenantAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, formatDate, getStatusColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Home, FileText, CreditCard, Bell, Building2, DoorOpen, Calendar, Loader2, AlertCircle, CheckCircle, Star, Clock, Info, ExternalLink } from 'lucide-react';
import { RatingDialog } from '../../components/ui/rating-dialog';
import { toast } from 'sonner';

export default function TenantDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [selectedLease, setSelectedLease] = useState(null);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [onlinePayLink, setOnlinePayLink] = useState(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  useEffect(() => {
    loadDashboard();
    loadPendingRatings();
    loadOnlinePayLink();
  }, []);

  const loadOnlinePayLink = async () => {
    try {
      const res = await tenantAPI.getLandlordPaymentMethods();
      const link = res.data?.payment_methods?.online_payment_link;
      if (link?.enabled && link?.url) {
        const url = /^https?:\/\//i.test(link.url.trim()) ? link.url.trim() : `https://${link.url.trim()}`;
        setOnlinePayLink(url);
      }
    } catch {
      // No payment link configured — button simply not shown
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await tenantAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRatings = async () => {
    try {
      const response = await tenantAPI.getPendingRatings();
      setPendingRatings(response.data);
    } catch (error) {
      console.error('Error loading pending ratings:', error);
    }
  };

  const handleRateClick = (lease) => {
    setSelectedLease(lease);
    setShowRatingDialog(true);
  };

  const handleSubmitRating = async (ratingData) => {
    try {
      setSubmittingRating(true);
      await tenantAPI.submitRating(ratingData);
      toast.success(t('ratings.submitSuccess'));
      setShowRatingDialog(false);
      setSelectedLease(null);
      loadPendingRatings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setSubmittingRating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const lease = dashboard?.active_lease;
  const pendingLease = dashboard?.pending_lease;
  const payment = dashboard?.current_month_payment;
  const paymentSchedule = dashboard?.payment_schedule;

  // Helper function to get ordinal suffix
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Generate payment due date message based on schedule type
  const getPaymentDueMessage = () => {
    if (!paymentSchedule) return null;
    
    const type = paymentSchedule.schedule_type;
    
    switch (type) {
      case 'fixed':
        const fixedDay = paymentSchedule.fixed_day || 5;
        return `Rent is due on the ${fixedDay}${getOrdinalSuffix(fixedDay)} of each month`;
      case 'window':
        const startDay = paymentSchedule.window_start_day || 1;
        const endDay = paymentSchedule.window_end_day || 5;
        return `Pay rent between the ${startDay}${getOrdinalSuffix(startDay)} and ${endDay}${getOrdinalSuffix(endDay)} of each month`;
      case 'bimonthly':
        const firstDay = paymentSchedule.bimonthly_first_day || 1;
        const secondDay = paymentSchedule.bimonthly_second_day || 15;
        return `Rent is due on the ${firstDay}${getOrdinalSuffix(firstDay)} and ${secondDay}${getOrdinalSuffix(secondDay)} of each month`;
      case 'per_lease':
        return 'Payment due date is specified in your lease agreement';
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="tenant-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">{t('dashboard.welcomeBack')}!</h1>
          <p className="text-muted-foreground mt-1">{t('dashboard.rentalOverview')}</p>
        </div>
        {lease && onlinePayLink && (
          <a
            href={onlinePayLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C85A42] hover:bg-[#B04A35] text-white text-sm font-medium px-4 py-2.5 transition-colors shrink-0"
            data-testid="dashboard-pay-online-btn"
          >
            <CreditCard className="w-4 h-4" />
            Pay Online
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      {/* Pending Verification Banner (newly registered tenants) */}
      {user && !user.is_verified && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800" data-testid="pending-verification-banner">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300">Waiting for Landlord Verification</h3>
                <p className="text-blue-700 dark:text-blue-400 text-sm mt-1">
                  We've notified your landlord that you've signed up. Once they verify your account and assign you a unit, you'll see your lease, rent details, and payment history here. This typically takes less than 24 hours.
                </p>

                {/* What happens next — milestone timeline */}
                <div className="mt-4 rounded-lg bg-white/60 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 p-3" data-testid="tenant-onboarding-timeline">
                  <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2 uppercase tracking-wide">What happens next</p>
                  <ol className="space-y-1.5">
                    {[
                      { label: 'Registered', done: true },
                      { label: 'Landlord verifies your account', done: false, current: true },
                      { label: 'Unit assigned', done: false },
                      { label: 'Lease issued — review & acknowledge', done: false },
                      { label: 'Move in', done: false },
                    ].map((step) => (
                      <li key={step.label} className="flex items-center gap-2 text-xs">
                        {step.done ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        ) : step.current ? (
                          <div className="w-4 h-4 rounded-full border-2 border-blue-500 bg-blue-100 dark:bg-blue-800 shrink-0 animate-pulse" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border border-blue-300 dark:border-blue-700 shrink-0" />
                        )}
                        <span
                          className={
                            step.done
                              ? 'text-emerald-700 dark:text-emerald-300 font-medium'
                              : step.current
                              ? 'text-blue-800 dark:text-blue-200 font-medium'
                              : 'text-blue-600/70 dark:text-blue-400/70'
                          }
                        >
                          {step.label}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>

                <p className="text-xs text-blue-600 dark:text-blue-400 mt-3">
                  Want to be verified faster? Landlords approve faster when they can see who's moving in. Complete your{' '}
                  <button
                    onClick={() => navigate('/profile')}
                    className="underline font-medium hover:text-blue-800"
                    data-testid="complete-profile-link"
                  >
                    profile
                  </button>
                  {' '}— school, ID, emergency contact — and your landlord will get a complete view at a glance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Lease Alert */}
      {pendingLease && !lease && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 dark:text-amber-300">{t('leases.pendingLease')}</h3>
                <p className="text-amber-700 dark:text-amber-400 text-sm mt-1">
                  {t('leases.pendingLeaseMessage')} {pendingLease.property?.name}, {t('units.room')} {pendingLease.unit?.unit_number}
                </p>
                <Button
                  className="mt-3 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/tenant/lease')}
                >
                  {t('leases.viewAndAcknowledge')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rating Prompt for Ended Leases */}
      {pendingRatings.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800" data-testid="rating-prompt-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-300">{t('ratings.rateExperience')}</h3>
                <p className="text-yellow-700 dark:text-yellow-400 text-sm mt-1">
                  {pendingRatings.length === 1 
                    ? t('ratings.leaseEndedMessage').replace('{property}', pendingRatings[0].property?.name)
                    : t('ratings.multipleRatingsMessage').replace('{count}', pendingRatings.length)}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {pendingRatings.map((lease) => (
                    <Button
                      key={lease.lease_id}
                      className="bg-yellow-600 hover:bg-yellow-700"
                      onClick={() => handleRateClick(lease)}
                      data-testid={`rate-lease-${lease.lease_id}`}
                    >
                      <Star className="w-4 h-4 mr-1" />
                      {t('ratings.rate')} {lease.property?.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Due Date Banner */}
      {lease && paymentSchedule && getPaymentDueMessage() && (
        <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-800" data-testid="payment-due-banner">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-emerald-800 dark:text-emerald-300">{t('payments.schedule')}</h3>
                  <Info className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-emerald-700 dark:text-emerald-400 text-sm">
                  {getPaymentDueMessage()}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900"
                onClick={() => navigate('/tenant/payments')}
                data-testid="make-payment-btn"
              >
                <CreditCard className="w-4 h-4 mr-1" />
                {t('payments.payNow')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Lease */}
      {lease ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-[#6B8E6B]" />
              {t('tenants.currentResidence')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Building2 className="w-4 h-4" />
                  {t('nav.properties')}
                </div>
                <p className="font-semibold">{lease.property?.name}</p>
                <p className="text-sm text-muted-foreground">{lease.property?.address}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <DoorOpen className="w-4 h-4" />
                  {t('nav.units')}
                </div>
                <p className="font-semibold">{/^\s*(room|rm)\b/i.test(lease.unit?.unit_number || '') ? lease.unit?.unit_number : `${t('units.room')} ${lease.unit?.unit_number || ''}`}</p>
                {lease.unit?.floor && <p className="text-sm text-muted-foreground">{lease.unit.floor} {t('units.floor')}</p>}
              </div>
              <div>
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <Calendar className="w-4 h-4" />
                  {t('leases.leasePeriod')}
                </div>
                <p className="font-semibold">{formatDate(lease.start_date)}</p>
                <p className="text-sm text-muted-foreground">{t('common.to')} {formatDate(lease.end_date)}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <CreditCard className="w-4 h-4" />
                  {t('leases.monthlyRent')}
                </div>
                <p className="font-semibold text-2xl text-[#C85A42]">{formatCurrency(lease.total_monthly_rent || lease.monthly_rent || lease.base_rent || 0)}</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm text-muted-foreground">{t('tenants.landlord')}:</p>
                <p className="font-medium">{lease.landlord?.name}</p>
              </div>
              {lease.landlord?.phone && (
                <p className="text-sm text-muted-foreground">{t('common.contact')}: {lease.landlord.phone}</p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : !pendingLease ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Home className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">{t('leases.noActiveLease')}</h3>
            <p className="text-muted-foreground">
              {t('leases.noActiveLeaseMessage')}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Quick Stats */}
      {lease && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Payment Status */}
          <Card className="cursor-pointer card-hover" onClick={() => navigate('/tenant/payments')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('payments.thisMonth')}</p>
                  {payment ? (
                    <>
                      <Badge className={`mt-2 ${getStatusColor(payment.status)}`}>{t(`status.${payment.status}`)}</Badge>
                      <p className="font-semibold mt-1">{formatCurrency(payment.amount)}</p>
                    </>
                  ) : (
                    <>
                      <Badge className="mt-2 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">{t('status.unpaid')}</Badge>
                      <p className="text-sm text-muted-foreground mt-1">{t('payments.submitPayment')}</p>
                    </>
                  )}
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${payment?.status === 'verified' ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-amber-100 dark:bg-amber-900'}`}>
                  {payment?.status === 'verified' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <CreditCard className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lease Info */}
          <Card className="cursor-pointer card-hover" onClick={() => navigate('/tenant/lease')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('leases.leaseStatus')}</p>
                  <Badge className="mt-2 bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">{t(`status.${lease.status}`)}</Badge>
                  <p className="text-sm text-muted-foreground mt-1">{t('common.viewDetails')}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#6B8E6B]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#6B8E6B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="cursor-pointer card-hover" onClick={() => navigate('/tenant/messages')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('nav.messages')}</p>
                  {dashboard?.unread_messages > 0 ? (
                    <>
                      <Badge className="mt-2 bg-[#C85A42] text-white">{dashboard.unread_messages} {t('messages.unread')}</Badge>
                      <p className="text-sm text-muted-foreground mt-1">{t('messages.checkInbox')}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold mt-2">{t('messages.noUnread')}</p>
                      <p className="text-sm text-muted-foreground">{t('messages.allCaughtUp')}</p>
                    </>
                  )}
                </div>
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center relative">
                  <Bell className="w-5 h-5 text-indigo-600" />
                  {dashboard?.unread_messages > 0 && (
                    <span className="notification-dot" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Dialog */}
      {selectedLease && (
        <RatingDialog
          open={showRatingDialog}
          onOpenChange={setShowRatingDialog}
          lease={selectedLease}
          onSubmit={handleSubmitRating}
          loading={submittingRating}
        />
      )}
    </div>
  );
}
